<script setup lang="ts">
import type {Author} from '~/repository/types';
import {ref} from 'vue';
import {useTimeoutFn} from '@vueuse/core';

const {profile, owner} = defineProps<{
  profile: Author,
  owner?: boolean,
}>();
const {t} = useI18n()
const isHovered = ref(false);

const {start: startHover, stop: stopHover} = useTimeoutFn(
  () => {
    isHovered.value = true;
  },
  50
);

const {start: startLeave, stop: stopLeave} = useTimeoutFn(
  () => {
    isHovered.value = false;
  },
  10
);

const handleMouseEnter = () => {
  stopLeave();
  startHover();
};

const handleMouseLeave = (event: MouseEvent) => {
  stopHover();
  const relatedTarget = event.relatedTarget as Node;
  if (relatedTarget && (relatedTarget.closest('.hover-card-content') || relatedTarget.closest('.trigger-element'))) {
    return;
  }
  startLeave();
};

const handleCardMouseEnter = () => {
  stopLeave();
  isHovered.value = true;
};

const handleCardMouseLeave = () => {
  isHovered.value = false;
};

const {formatNumber} = useFormatNumber();
const formattedFollowers = computed(() => formatNumber(profile.stats.followers));
</script>

<template>
  <HoverCard>
    <HoverCardTrigger as-child>
      <NuxtLink
        :to="{ name: 'username', params: { username: profile.username } }"
        class="cursor-pointer hover:underline trigger-element pb-1"
        :class="$attrs.class"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        {{ profile.name }}
      </NuxtLink>
    </HoverCardTrigger>
    <div
      class="hover-card-content"
      @mouseenter="handleCardMouseEnter"
      @mouseleave="handleCardMouseLeave"
    >
      <HoverCardContent v-if="isHovered" class="w-80 z-[54] border-[0.5px] border-gray-400 hover-card-content">
        <NuxtLink
          :to="{ name: 'username', params: { username: profile.username } }"
          class="flex justify-between items-center space-x-4 cursor-pointer"
        >
          <div>
            <h3 class="text-xl font-semibold">{{ profile.name }}</h3>
            <div>{{ profile.username }}</div>
          </div>
          <UserAvatar :user="profile" class="w-16 h-16"/>
        </NuxtLink>
        <div class="mt-2">{{ profile.bio }}</div>
        <div class="text-gray mt-2">{{ formattedFollowers }} {{ t('buttons.followers') }}</div>
        <UserFollowButton class="mt-4" v-if="!owner" :profile="profile"/>
      </HoverCardContent>
    </div>
  </HoverCard>
</template>
